
import { create } from 'zustand';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/stores/auth';
import { ROLE_NAMES, type RoleName } from '@/lib/constants/roles';
import type { SharedStream } from '@/lib/shared-types';
import type { StreamListItem, StreamDetail } from '@/lib/shared-types/api/endpoints/streams';

// Type guard for RoleName
const isRoleName = (role: string): role is RoleName => {
  return Object.values(ROLE_NAMES).includes(role as RoleName);
};

// Type guard for privileged roles
const isPrivilegedRole = (role: RoleName): boolean => {
  const privilegedRoles: RoleName[] = [ROLE_NAMES.ADMIN, ROLE_NAMES.MEDIA_TEAM, ROLE_NAMES.MODERATOR];
  return privilegedRoles.includes(role);
};

// Backend response types (before transformation)
interface BackendStreamListItem extends Omit<StreamListItem, 'tags'> {
  tags_array: string[];
}

interface BackendStreamDetail extends Omit<StreamDetail, 'tags'> {
  tags_array: string[];
}

interface BackendStreamListData {
  streams: BackendStreamListItem[];
  total: number;
}

interface BackendStreamDetailData {
  stream: BackendStreamDetail;
}
import type {
  StreamsStore,
  Stream,
  StreamFilters,
  CreateStreamData,
  UpdateStreamData,
  StreamsResponse,
  StreamResponse
} from './types';

// API functions
const apiFetchStreams = async (filters?: Partial<StreamFilters>, page = 1, pageSize = 20): Promise<StreamsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (filters?.status) params.set('status', filters.status);
  if (filters?.visibility) params.set('visibility', filters.visibility);
  if (filters?.searchQuery) params.set('search', filters.searchQuery);

  const response = await api.get<BackendStreamListData>(`api/streams?${params}`);

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to fetch streams');
  }

  // Transform backend response to expected frontend format
  const backendData = response.data!;
  const streams = (backendData.streams || []).map((stream: BackendStreamListItem): Stream => ({
    ...stream,
    tags: stream.tags_array || []
  }));

  // Debug logging (only in development)
  if (import.meta.env.DEV) {
    console.log('apiFetchStreams - raw backend streams:', backendData.streams);
    console.log('apiFetchStreams - transformed streams:', streams);
    streams.forEach((stream, index) => {
      console.log(`Stream ${index}: id=${stream.id} (${typeof stream.id}), title=${stream.title}`);
    });
  }

  return {
    streams,
    pagination: {
      page,
      pageSize,
      total: backendData.total || 0,
      hasMore: (page * pageSize) < (backendData.total || 0)
    }
  };
};

const apiFetchStream = async (id: number): Promise<StreamResponse> => {
  const response = await api.get<BackendStreamDetailData>(`api/streams/${id}`);

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to fetch stream');
  }

  const data = response.data!;
  return {
    stream: {
      ...data.stream,
      tags: data.stream.tags_array || []
    } as Stream,
    access: {
      hasAccess: data.stream.has_access || false,
      accessType: 'public' as const,
    }
  };
};

const apiCreateStream = async (data: CreateStreamData): Promise<Stream> => {
  const response = await api.post<{ stream: Stream }>('streams', data);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to create stream');
  }
  if (!response.data?.stream) {
    throw new Error('Invalid response: no stream data');
  }
  return response.data.stream;
};

const apiUpdateStream = async (id: number, data: UpdateStreamData): Promise<void> => {
  const response = await api.put(`api/streams/${id}`, data);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to update stream');
  }
};

const apiDeleteStream = async (id: number): Promise<void> => {
  const response = await api.delete(`api/streams/${id}`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to delete stream');
  }
};

const apiLikeStream = async (streamId: number): Promise<void> => {
  const response = await api.post(`api/streams/${streamId}/likes`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to like stream');
  }
};

const apiRequestAccess = async (streamId: number, message?: string): Promise<void> => {
  const response = await api.post(`api/streams/${streamId}/access-request`, { reason: message });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to request access');
  }
};

const apiJoinWithInvite = async (code: string): Promise<{ message: string; stream?: SharedStream }> => {
  const response = await api.post<{ message: string; stream?: SharedStream }>('invites/join', { code });
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to join with invite');
  }
  if (!response.data) {
    throw new Error('Invalid response: no data');
  }
  return response.data;
};

export const useStreamsStore = create<StreamsStore>((set, get) => ({
  // Initial state
  streams: [],
  currentStream: null,
  currentStreamAccess: null,
  isLoading: false,
  error: null,
  isRealtimeConnected: false,
  filters: {
    sortBy: 'createdAt',
    sortOrder: 'desc'
  },
  pagination: {
    page: 1,
    pageSize: 20,
    total: 0,
    hasMore: false
  },

  // Actions
  fetchStreams: async (filters) => {
    try {
      set({ isLoading: true, error: null });

      if (filters) {
        set((state) => ({
          filters: { ...state.filters, ...filters }
        }));
      }

      const currentFilters = get().filters;
      const response = await apiFetchStreams(currentFilters);

      set({
        streams: response.streams,
        pagination: response.pagination,
        isLoading: false,
        error: null
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch streams';
      set({
        error: errorMessage,
        isLoading: false,
        streams: []
      });
    }
  },

  fetchStream: async (id) => {
    try {
      set({ isLoading: true, error: null });

      const response = await apiFetchStream(id);

      set({
        currentStream: response.stream,
        currentStreamAccess: response.access,
        isLoading: false,
        error: null
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch stream';
      set({
        error: errorMessage,
        isLoading: false,
        currentStream: null,
        currentStreamAccess: null
      });
    }
  },

  fetchStreamAccess: async (streamId) => {
    try {
      // This would typically be a separate API call to check access
      const response = await apiFetchStream(streamId);

      set({
        currentStreamAccess: response.access
      });

    } catch (error) {
      console.error('Failed to fetch stream access:', error);
      throw error;
    }
  },

  createStream: async (data) => {
    try {
      set({ isLoading: true, error: null });

      const newStream = await apiCreateStream(data);

      // Add to streams list
      set((state) => ({
        streams: [newStream, ...state.streams],
        isLoading: false,
        error: null
      }));

      return newStream;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create stream';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  updateStream: async (id, data) => {
    try {
      set({ isLoading: true, error: null });

      await apiUpdateStream(id, data);

      // Update in streams list
      set((state) => ({
        streams: state.streams.map(stream =>
          stream.id === id ? { ...stream, ...data, updatedAt: new Date().toISOString() } : stream
        ),
        currentStream: state.currentStream?.id === id
          ? { ...state.currentStream, ...data, updatedAt: new Date().toISOString() }
          : state.currentStream,
        isLoading: false,
        error: null
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update stream';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  deleteStream: async (id) => {
    try {
      set({ isLoading: true, error: null });

      await apiDeleteStream(id);

      // Remove from streams list
      set((state) => ({
        streams: state.streams.filter(stream => stream.id !== id),
        currentStream: state.currentStream?.id === id ? null : state.currentStream,
        isLoading: false,
        error: null
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete stream';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...(filters as Partial<StreamFilters>) },
      pagination: { ...state.pagination, page: 1 } // Reset to first page when filters change
    }));
  },

  loadMoreStreams: async () => {
    const state = get();
    if (state.isLoading || !state.pagination.hasMore) return;

    try {
      set({ isLoading: true });

      const nextPage = state.pagination.page + 1;
      const response = await apiFetchStreams(state.filters, nextPage, state.pagination.pageSize);

      set((currentState) => ({
        streams: [...currentState.streams, ...response.streams],
        pagination: response.pagination,
        isLoading: false
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load more streams';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  resetStreams: () => {
    set({
      streams: [],
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        hasMore: false
      }
    });
  },

  likeStream: async (streamId) => {
    try {
      await apiLikeStream(streamId);

      // Optimistically update the stream's like status or fetch fresh data
      set((state) => ({
        currentStream: state.currentStream?.id === streamId
          ? { ...state.currentStream, likes: (state.currentStream.likes || 0) + 1 }
          : state.currentStream
      }));

    } catch (error) {
      console.error('Failed to like stream:', error);
      throw error;
    }
  },

  unlikeStream: async (streamId) => {
    try {
      await apiLikeStream(streamId); // Backend toggle

      // Optimistically update the stream's like status
      set((state) => ({
        currentStream: state.currentStream?.id === streamId
          ? { ...state.currentStream, likes: Math.max((state.currentStream.likes || 0) - 1, 0) }
          : state.currentStream
      }));

    } catch (error) {
      console.error('Failed to unlike stream:', error);
      throw error;
    }
  },

  requestAccess: async (streamId, message) => {
    try {
      set({ isLoading: true, error: null });

      await apiRequestAccess(streamId, message);

      set({ isLoading: false });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to request access';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  useInviteCode: async (streamId, code) => {
    try {
      set({ isLoading: true, error: null });

      const result = await apiJoinWithInvite(code);

      // Update access status based on backend response
      set({
        currentStreamAccess: {
          hasAccess: true,
          accessType: 'invite',
          restrictions: {
            regionAllowed: true,
            inviteValid: true,
            rolePermitted: true
          },
          inviteCode: code,
          accessGrantedAt: new Date().toISOString()
        },
        isLoading: false
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Invalid invite code';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  setCurrentStream: (stream) => {
    set({ currentStream: stream });
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  setError: (error) => {
    set({ error, isLoading: false });
  },

  clearError: () => {
    set({ error: null });
  },

  // Real-time (Supabase Realtime)
  connectToStreamsRealtime: async () => {
    try {
      const { supabase } = await import('@/lib/supabase/client');

      // Unsubscribe any existing streams channel first
      const existing = supabase.getChannels();
      for (const ch of existing) {
        if (ch.topic.startsWith('realtime:streams-')) {
          await supabase.removeChannel(ch);
        }
      }

      supabase
        .channel('streams-public')
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'streams',
          },
          (payload) => {
            const updated = payload.new as any;
            set((state) => ({
              streams: state.streams.map((s) =>
                s.id === updated.id ? { ...s, ...updated } : s
              ),
              currentStream:
                state.currentStream?.id === updated.id
                  ? { ...state.currentStream, ...updated }
                  : state.currentStream,
            }));
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'streams',
          },
          (payload) => {
            const newStream = payload.new as any;
            set((state) => ({
              streams: [newStream as Stream, ...state.streams],
            }));
          }
        )
        .subscribe((status) => {
          console.log(`[Realtime] streams channel status: ${status}`);
          set({ isRealtimeConnected: status === 'SUBSCRIBED' });
        });
    } catch (error) {
      console.error('Failed to connect to streams Realtime:', error);
    }
  },

  disconnectFromStreamsRealtime: async () => {
    try {
      const { supabase } = await import('@/lib/supabase/client');
      const channels = supabase.getChannels();
      for (const ch of channels) {
        if (ch.topic.startsWith('realtime:streams-')) {
          await supabase.removeChannel(ch);
        }
      }
    } catch { /* ignore */ }
    set({ isRealtimeConnected: false });
  },
}));

// Selectors for common streams state
export const useStreams = () => useStreamsStore((state) => state.streams);
export const useCurrentStream = () => useStreamsStore((state) => state.currentStream);
export const useCurrentStreamAccess = () => useStreamsStore((state) => state.currentStreamAccess);
export const useStreamsLoading = () => useStreamsStore((state) => state.isLoading);
export const useStreamsError = () => useStreamsStore((state) => state.error);
export const useStreamsFilters = () => useStreamsStore((state) => state.filters);
export const useStreamsPagination = () => useStreamsStore((state) => state.pagination);

// Helper hooks for stream status
export const useLiveStreams = () => {
  const streams = useStreams();
  return streams.filter(stream => stream.status === 'live');
};

export const useScheduledStreams = () => {
  const streams = useStreams();
  return streams.filter(stream => stream.status === 'scheduled');
};

export const useEndedStreams = () => {
  const streams = useStreams();
  return streams.filter(stream => stream.status === 'ended');
};

// Helper hook to check if user has access to current stream
export const useHasStreamAccess = () => {
  const access = useCurrentStreamAccess();
  return access?.hasAccess || false;
};

// Helper hook for stream actions
export const useCanCreateStream = () => {
  const { user, isAuthenticated } = useAuthStore.getState();

  if (!isAuthenticated || !user) {
    return false;
  }

  const userRoleName = user.role?.name || ROLE_NAMES.MEMBER;
  const userRole: RoleName = isRoleName(userRoleName) ? userRoleName : ROLE_NAMES.MEMBER;
  // Allow media team, admin, and moderator to create streams
  return userRole === ROLE_NAMES.ADMIN ||
         userRole === ROLE_NAMES.MEDIA_TEAM ||
         userRole === ROLE_NAMES.MODERATOR;
};

export const useCanEditStream = (streamId?: number) => {
  const { user, isAuthenticated } = useAuthStore.getState();
  const currentStream = useCurrentStream();
  const stream = streamId ? currentStream : currentStream;

  if (!isAuthenticated || !user || !stream) {
    return false;
  }

  const userRoleName = user.role?.name || ROLE_NAMES.MEMBER;
  const userRole: RoleName = isRoleName(userRoleName) ? userRoleName : ROLE_NAMES.MEMBER;
  const isAdmin = userRole === ROLE_NAMES.ADMIN;
  const isCreator = stream.createdBy === user.id;

  // Admin can edit any stream, creator can edit their own stream
  return isAdmin || isCreator;
};
